import React from 'react';
import { useNavigate } from 'react-router-dom';
// Toaster removed - using global Toaster from App.js
import { BACKEND_URL } from '../../hooks/useDashboard/dashboardApi';
import { showError } from '../../utils/sweetAlert';
import logger from '../../utils/logger';

/**
 * AssessmentCardSkeleton
 *
 * Placeholder skeleton for loading state of assessment cards.
 */
export const AssessmentCardSkeleton = () => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800 animate-pulse">
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-1" />
    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full w-32 mb-4" />
    <div className="space-y-2">
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
    </div>
  </div>
);

/**
 * AssessmentCard
 *
 * Props:
 * - assessment: assessment object
 * - userEmail: fallback contact email
 */
const AssessmentCard = ({ assessment, userEmail }) => {
  const navigate = useNavigate();
  const isEMHA = assessment.assessment_type === 'EMHA';
  const isPaid = assessment.payment_status === 'completed';
  const reportAvailable = assessment.report_available;
  const reportGenerating = isPaid && !reportAvailable;

  const downloadReport = async () => {
    try {
      // Handle report_url that may or may not include /api/ prefix
      let reportUrl = assessment.report_url || '';
      if (reportUrl.startsWith('/api/')) {
        // Remove /api/ prefix if present (backend might include it)
        reportUrl = reportUrl.replace(/^\/api\//, '');
      }
      const downloadUrl = `${BACKEND_URL}/api/${reportUrl}`;
      
      logger.log(`📥 Attempting to download report from: ${downloadUrl}`);
      logger.log(`📋 Assessment data:`, { 
        report_url: assessment.report_url, 
        report_filename: assessment.report_filename,
        payment_id: assessment.payment_id 
      });
      
      const response = await fetch(downloadUrl, { credentials: 'include' });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download =
          assessment.report_filename ||
          `energy-report-${assessment.facility_name}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        logger.log('✅ Report downloaded successfully');
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        logger.error(`❌ Download failed: ${response.status} ${response.statusText}`, errorText);
        await showError(
          'Download Failed',
          `Failed to download report (${response.status}). ${response.status === 404 ? 'Report may not be available yet. Please contact support if this issue persists.' : 'Please try again or contact support.'}`
        );
      }
    } catch (error) {
      logger.error('Download error:', error);
      await showError(
        'Download Error',
        `Error downloading report: ${error.message}. Please check your connection and try again.`
      );
    }
  };

  const viewEMHAResults = () => {
    navigate(`/emha/results/${assessment.id}`);
  };

  const purchaseReport = () => {
    const email = assessment.contact_email || userEmail || '';
    navigate(
      `/payment/checkout?assessment_id=${assessment.id}&email=${encodeURIComponent(
        email
      )}`
    );
  };

  const viewDetails = () => {
    navigate(`/assessment/results/${assessment.id}`);
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-gray-800">
      {/* Toaster removed - using global Toaster from App.js */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {assessment.facility_name}
          </h3>
          <div className="flex gap-2">
            {isEMHA && (
              <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs font-semibold">
                EMHA
              </span>
            )}
            {!isEMHA && (
              <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs font-semibold">
                ESA
              </span>
            )}
            {(assessment.source === 'live_session' ||
              (assessment.metadata &&
                (typeof assessment.metadata === 'string'
                  ? assessment.metadata.includes('consultant-session')
                  : assessment.metadata.source === 'consultant-session'))) && (
              <span className="bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded text-xs font-semibold">
                Consultant-Led
              </span>
            )}
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
          {assessment.industry_name}
        </p>
        <p className="text-gray-500 dark:text-gray-500 text-xs">
          Completed:{' '}
          {new Date(
            assessment.completion_date || assessment.created_at
          ).toLocaleDateString()}
        </p>
      </div>

      {isEMHA && (
        <div className="mb-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-3 rounded-lg mb-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Maturity Score:
              </span>
              <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {assessment.overall_score?.toFixed(1) || assessment.metadata?.overall_score?.toFixed(1) || '0.0'}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                LEAP Phase:
              </span>
              <span className="font-semibold text-purple-700 dark:text-purple-300">
                {assessment.leap_phase || assessment.metadata?.leap_phase || 'Measure'}
              </span>
            </div>
          </div>
          <button
            onClick={viewEMHAResults}
            className="w-full bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                fillRule="evenodd"
                d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z"
                clipRule="evenodd"
              />
            </svg>
            View Results
          </button>
        </div>
      )}

      {!isEMHA && (
        <>
          <div className="mb-4">
            {isPaid && reportAvailable && (
              <div className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-sm font-medium">
                ✅ Report Ready
              </div>
            )}
            {reportGenerating && (
              <div className="inline-block bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-3 py-1 rounded-full text-sm font-medium">
                ⏳ Report Generating...
              </div>
            )}
            {assessment.payment_status === 'pending' && (
              <div className="inline-block bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-3 py-1 rounded-full text-sm font-medium">
                💰 Payment Pending
              </div>
            )}
            {!isPaid && !assessment.payment_status && (
              <div className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full text-sm font-medium">
                📋 Assessment Complete
              </div>
            )}
          </div>

          <div className="space-y-2">
            {reportAvailable ? (
              <>
                <button
                  onClick={downloadReport}
                  className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Report
                </button>
                <button
                  onClick={viewDetails}
                  className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path
                      fillRule="evenodd"
                      d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  View Details
                </button>
              </>
            ) : isPaid ? (
              <button
                disabled
                className="w-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 py-2 px-4 rounded-lg flex items-center justify-center gap-2 font-medium cursor-not-allowed"
              >
                <div className="w-4 h-4 border-2 border-yellow-800 dark:border-yellow-200 border-t-transparent rounded-full animate-spin" />
                Generating Report...
              </button>
            ) : (
              <button
                onClick={purchaseReport}
                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Purchase Report ($990)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentCard;


